import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'
import type { PageMeta } from '@/lib/types'

export interface PostAuthor {
  id: string
  full_name: string
  avatar_url?: string
  headline?: string
}

export type AttachmentKind = 'service' | 'course' | 'vacancy' | 'contest' | 'job'

/** Produk atau jasa yang ditempelkan pada sebuah kiriman. */
export interface PostAttachment {
  kind: AttachmentKind
  kind_label: string
  id: string
  title: string
  subtitle?: string
  price_label?: string
  price: number
  image_url?: string
  href: string
}

export interface Post {
  id: string
  author: PostAuthor
  group_id?: string
  group_name?: string
  group_slug?: string
  body: string
  images: string[]
  like_count: number
  comment_count: number
  attachment?: PostAttachment
  liked: boolean
  can_delete: boolean
  created_at: string
}

export interface PostComment {
  id: string
  author: PostAuthor
  body: string
  can_delete: boolean
  created_at: string
}

export interface Group {
  id: string
  slug: string
  name: string
  description?: string
  cover_url?: string
  privacy: 'public' | 'private'
  member_count: number
  post_count: number
  my_role?: 'owner' | 'admin' | 'member'
  can_read: boolean
  can_manage: boolean
  can_dissolve: boolean
  created_at: string
}

export interface GroupMember {
  user: PostAuthor
  role: string
  joined_at: string
}

type FeedTab = 'mengikuti' | 'jelajah'

/**
 * useFeed memuat lini masa halaman demi halaman.
 *
 * Memakai kueri tak berhingga, bukan paginasi bernomor: lini masa dibaca
 * menerus ke bawah, dan nomor halaman pada aliran yang isinya bertambah dari
 * atas justru membuat kiriman yang sama muncul dua kali.
 */
export function useFeed(tab: FeedTab) {
  return useInfiniteQuery({
    queryKey: ['feed', tab],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      api.get<{ posts: Post[]; page: number }>(tab === 'jelajah' ? '/feed/jelajah' : '/feed', {
        query: { page: pageParam, per_page: 12 },
      }),
    getNextPageParam: (last) => (last.posts.length < 12 ? undefined : last.page + 1),
  })
}

export function useGroupFeed(slug: string) {
  return useQuery({
    queryKey: ['group-feed', slug],
    queryFn: () =>
      api.get<{ group: Group; posts: Post[] }>(`/groups/${slug}/posts`, { query: { per_page: 30 } }),
    // Grup tertutup menjawab 403 bagi yang belum bergabung. Itu jawaban yang
    // benar, bukan kegagalan yang perlu dicoba ulang.
    retry: false,
  })
}

export function useUserPosts(userId: string | undefined) {
  return useQuery({
    queryKey: ['user-posts', userId],
    queryFn: () => api.get<{ posts: Post[] }>(`/users/${userId}/posts`, { query: { per_page: 20 } }),
    enabled: Boolean(userId),
  })
}

export interface CreatePostInput {
  body: string
  images?: string[]
  group_id?: string
  attachment_kind?: AttachmentKind
  attachment_id?: string
}

/**
 * useMyListings membaca produk dan jasa yang dapat dilampirkan pemanggil.
 *
 * Dimuat sekali lalu disimpan cukup lama: daftar ini jarang berubah dalam
 * satu kunjungan, dan memuatnya ulang setiap kali pemilih dibuka membuat
 * kotak tulis terasa berat.
 */
export function useMyListings() {
  return useQuery({
    queryKey: ['my-listings'],
    queryFn: () => api.get<{ listings: PostAttachment[] }>('/me/listings'),
    staleTime: 5 * 60 * 1000,
  })
}

function segarkanPostingan(client: ReturnType<typeof useQueryClient>) {
  client.invalidateQueries({ queryKey: ['feed'] })
  client.invalidateQueries({ queryKey: ['group-feed'] })
  client.invalidateQueries({ queryKey: ['user-posts'] })
}

export function useCreatePost() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: CreatePostInput) => api.post<{ post: Post }>('/posts', input),
    onSuccess: () => segarkanPostingan(client),
  })
}

export function useDeletePost() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (postId: string) => api.delete<void>(`/posts/${postId}`),
    onSuccess: () => segarkanPostingan(client),
  })
}

/**
 * useToggleLike menyalakan dan mematikan suka.
 *
 * Menyegarkan dilakukan setelah permintaannya selesai, berhasil maupun
 * gagal, sehingga angka yang terlihat selalu angka yang benar-benar
 * tersimpan — bukan tebakan yang perlu dikembalikan bila jaringan putus.
 */
export function useToggleLike() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({ postId, liked }: { postId: string; liked: boolean }) =>
      liked ? api.delete<void>(`/posts/${postId}/like`) : api.post<void>(`/posts/${postId}/like`),
    onSettled: () => segarkanPostingan(client),
  })
}

export function usePostComments(postId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['post-comments', postId],
    queryFn: () => api.get<{ comments: PostComment[] }>(`/posts/${postId}/comments`),
    enabled,
  })
}

export function useAddComment(postId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (body: string) => api.post<{ comment: PostComment }>(`/posts/${postId}/comments`, { body }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['post-comments', postId] })
      segarkanPostingan(client)
    },
  })
}

export function useDeleteComment(postId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (commentId: string) => api.delete<void>(`/comments/${commentId}`),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['post-comments', postId] })
      segarkanPostingan(client)
    },
  })
}

/** Mengunggah satu gambar postingan dan mengembalikan alamatnya. */
export function useUploadPostImage() {
  return useMutation({
    mutationFn: async (file: File) => {
      const body = new FormData()
      body.append('file', file)

      const response = await fetch('/api/v1/posts/image', {
        method: 'POST',
        credentials: 'include',
        body,
      })

      const payload = await response.json()
      if (!response.ok) {
        throw new Error(payload?.error?.message ?? 'Gambar gagal diunggah.')
      }
      return payload as { image_url: string }
    },
  })
}

// ---------------------------------------------------------------------------
// Grup
// ---------------------------------------------------------------------------

export function useGroups(query: string, page = 1) {
  return useQuery({
    queryKey: ['groups', query, page],
    queryFn: () =>
      api.get<{ groups: Group[]; meta: PageMeta }>('/groups', { query: { q: query, page, per_page: 12 } }),
  })
}

export function useMyGroups() {
  return useQuery({
    queryKey: ['my-groups'],
    queryFn: () => api.get<{ groups: Group[] }>('/me/groups'),
  })
}

export function useGroup(slug: string) {
  return useQuery({
    queryKey: ['group', slug],
    queryFn: () => api.get<{ group: Group }>(`/groups/${slug}`),
    select: (data) => data.group,
  })
}

export function useGroupMembers(slug: string, enabled: boolean) {
  return useQuery({
    queryKey: ['group-members', slug],
    queryFn: () => api.get<{ members: GroupMember[] }>(`/groups/${slug}/members`),
    enabled,
    retry: false,
  })
}

export interface GroupInput {
  name: string
  description?: string
  cover_url?: string
  privacy?: 'public' | 'private'
}

function segarkanGrup(client: ReturnType<typeof useQueryClient>) {
  client.invalidateQueries({ queryKey: ['groups'] })
  client.invalidateQueries({ queryKey: ['my-groups'] })
  client.invalidateQueries({ queryKey: ['group'] })
  client.invalidateQueries({ queryKey: ['group-feed'] })
}

export function useCreateGroup() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: GroupInput) => api.post<{ group: Group }>('/groups', input),
    onSuccess: () => segarkanGrup(client),
  })
}

export function useUpdateGroup(slug: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: GroupInput) => api.patch<{ group: Group }>(`/groups/${slug}`, input),
    onSuccess: () => segarkanGrup(client),
  })
}

export function useJoinGroup() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (slug: string) => api.post<{ group: Group }>(`/groups/${slug}/join`),
    onSuccess: () => segarkanGrup(client),
  })
}

export function useLeaveGroup() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (slug: string) => api.delete<void>(`/groups/${slug}/join`),
    onSuccess: () => segarkanGrup(client),
  })
}

/** useDissolveGroup membubarkan grup beserta seluruh kirimannya. */
export function useDissolveGroup() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (slug: string) => api.delete<void>(`/groups/${slug}`),
    onSuccess: () => segarkanGrup(client),
  })
}

// ---------------------------------------------------------------------------
// Mengikuti
// ---------------------------------------------------------------------------

export interface FollowStats {
  followers: number
  following: number
  is_following: boolean
}

export function useFollowStats(userId: string | undefined) {
  return useQuery({
    queryKey: ['follow', userId],
    queryFn: () => api.get<{ follow: FollowStats }>(`/users/${userId}/follow`),
    select: (data) => data.follow,
    enabled: Boolean(userId),
  })
}

export function useToggleFollow(userId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (isFollowing: boolean) =>
      isFollowing
        ? api.delete<{ follow: FollowStats }>(`/users/${userId}/follow`)
        : api.post<{ follow: FollowStats }>(`/users/${userId}/follow`),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['follow', userId] })
      client.invalidateQueries({ queryKey: ['feed'] })
    },
  })
}
